import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { strokeSnowflakeSpokes } from "./snowflakeShapes";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const MOBILE_FLAKES_RATIO = 0.5;
const FLAKES_COUNT = 55;
const GLOBAL_ALPHA = 0.6;
const GLOW_RADIUS_RATIO = 2.4;

export type SnowflakeAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type FlakeParticle = {
  x: number;
  y: number;
  radius: number;
  speedY: number;
  swayAmplitude: number;
  swaySpeed: number;
  swayPhase: number;
  rotation: number;
  spin: number;
  depth: number;
};

export const SnowflakeAnimation: FC<SnowflakeAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function flakeColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(0, 0%, 82%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(0, 0%, 38%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function glowColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
        case "light":
          return `hsla(${hue}, 55%, 78%, ${alpha * 0.28})`;
        case "darkdull":
          return `hsla(0, 0%, 88%, ${alpha * 0.15})`;
        case "lightdull":
          return `hsla(0, 0%, 50%, ${alpha * 0.22})`;
        default:
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
      }
    }

    function glowEdgeColor(): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, 0)`;
        case "light":
          return `hsla(${hue}, 55%, 78%, 0)`;
        case "darkdull":
          return `hsla(0, 0%, 88%, 0)`;
        case "lightdull":
          return `hsla(0, 0%, 50%, 0)`;
        default:
          return `hsla(${hue}, 80%, 88%, 0)`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    function mkFlake(spawnAboveViewport: boolean): FlakeParticle {
      const height = canvasEl.height;
      const radius = 2.5 + Math.random() * 5.5;
      return {
        x: Math.random() * canvasEl.width,
        y: spawnAboveViewport ? -radius - Math.random() * height * 0.3 : Math.random() * height,
        radius,
        speedY: 0.3 + Math.random() * 0.55,
        swayAmplitude: 0.4 + Math.random() * 1.1,
        swaySpeed: 0.008 + Math.random() * 0.02,
        swayPhase: Math.random() * Math.PI * 2,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.012,
        depth: 0.45 + Math.random() * 0.55,
      };
    }

    const flakesCount = isMobile ? Math.round(FLAKES_COUNT * MOBILE_FLAKES_RATIO) : FLAKES_COUNT;
    const flakes: FlakeParticle[] = Array.from({ length: flakesCount }, () => mkFlake(false));

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, flakes);
    }

    window.addEventListener("resize", onResize);

    function drawFlake(flake: FlakeParticle, alpha: number): void {
      context.save();
      context.translate(flake.x, flake.y);

      const glowRadius = flake.radius * GLOW_RADIUS_RATIO;
      const glow = context.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
      glow.addColorStop(0, glowColor(alpha));
      glow.addColorStop(1, glowEdgeColor());
      context.fillStyle = glow;
      context.beginPath();
      context.arc(0, 0, glowRadius, 0, Math.PI * 2);
      context.fill();

      context.rotate(flake.rotation);
      context.strokeStyle = flakeColor(alpha);
      strokeSnowflakeSpokes(context, flake.radius);

      context.restore();
    }

    const isDarkDull = theme === "darkdull";
    let frame = 0;

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;

      for (const flake of flakes) {
        flake.y += flake.speedY * frameSteps;
        flake.x += Math.sin(frame * flake.swaySpeed + flake.swayPhase) * flake.swayAmplitude * 0.35 * frameSteps;
        flake.rotation += flake.spin * frameSteps;

        let alpha = GLOBAL_ALPHA * flake.depth;
        if (isDarkDull) {
          alpha = alpha * 1.9 + 0.2;
        }
        drawFlake(flake, Math.min(isDarkDull ? 1 : 0.85, alpha));

        if (flake.y - flake.radius * GLOW_RADIUS_RATIO > canvasEl.height) {
          Object.assign(flake, mkFlake(true));
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
